import { Tooltip, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { CheckCircle, ErrorRounded } from "@material-ui/icons";
import { get } from "lodash";
import React from "react";
import { Link } from "react-router-dom";
import { Booking, Delivery, ProductInstance } from "../types";
import { getStyle } from "../utils";
import { StatusColorContext } from "./contexts";
import { translateStatus } from "./StatusField";

interface Props {
  resource: { id: number; status: string };
}

interface MyChipProps {
  customResourceName?: string;
  customLabel?: string;
  resource: { id: number; status: string };
  resourceName: string;
  avatarCharacter?: string;
  style?: object;
  avatarAction?: string;
  tooltip?: React.ReactNode;
  isEOT?: boolean;
}
const MyChip: React.FC<MyChipProps> = ({
  customResourceName,
  customLabel,
  resource,
  resourceName,
  avatarCharacter,
  style,
  avatarAction,
  tooltip,
  isEOT,
}) => {
  const status = translateStatus(resource.status);

  const label =
    customLabel ||
    `${resource.id} ${customResourceName ? customResourceName : status}`;

  const initial = avatarCharacter ?? resourceName.substring(0, 2).toUpperCase();
  const { colors } = React.useContext(StatusColorContext);

  return (
    <Box display="inline-block" style={{ padding: 1 }}>
      <Link
        to={`/${resourceName}/${resource.id}/show`}
        style={{
          ...getStyle(customResourceName ? "revised" : resource.status, colors),
          textDecoration: "none",
          display: "flex",
          padding: "2px",
          borderRadius: "16px",
          fontSize: "0.7rem",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        <Box
          style={{
            backgroundColor: "white",
            color: "#333",
            fontSize: "0.6rem",
            fontWeight: "bold",
            borderRadius: "50%",
            padding: "5px",
          }}
        >
          {initial}
        </Box>

        <Box
          style={{
            padding: "3px 5px ",
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>

        {tooltip && (
          <Box
            style={{
              color: customResourceName ? "#fff" : "#333",
              fontSize: "0.6rem",
              fontWeight: "bold",
            }}
          >
            {tooltip}
          </Box>
        )}

        {avatarAction && (
          <Box
            style={{
              backgroundColor: "white",
              color: "#333",
              fontSize: "0.6rem",
              fontWeight: "bold",
              borderRadius: "50%",
              padding: "5px",
            }}
          >
            {avatarAction}
          </Box>
        )}

        {isEOT && (
          <Box
            style={{
              backgroundColor: "red",
              color: "#ffff",
              fontSize: "0.6rem",
              fontWeight: "bold",
              borderRadius: "50%",
              padding: "5px",
            }}
          >
            {process.env.REACT_APP_COMPANY_CODE === "STMY" ? "UD" : "EOT"}
          </Box>
        )}
      </Link>
    </Box>
  );
};

export const BookingChip: React.FC<{ resource: Booking }> = ({
  resource,
  ...rest
}) => {
  const hasIssues = resource.booking_issues?.length > 0;
  const hasRevisions = resource.revisions?.length > 0;
  const isNew = resource.status === "new";

  const isAllDeliveriesVerified =
    resource.deliveries &&
    resource.deliveries.length > 0 &&
    resource.deliveries.every((delivery: any) => delivery.verify_at);

  const renderTooltipIcon = () => {
    if (!hasIssues) return null;

    return (
      <Tooltip
        title={
          <React.Fragment>
            {resource.booking_issues.map((issue, index) => (
              <Typography variant="body2" key={issue.id} color="inherit">
                {index + 1}. {issue.status} - {issue.description}
              </Typography>
            ))}
          </React.Fragment>
        }
      >
        {resource.booking_issues.every((issue) => issue.status === "open") ? (
          <ErrorRounded color="error" />
        ) : resource.booking_issues.every(
            (issue) => issue.status === "accepted"
          ) ? (
          <CheckCircle color="primary" />
        ) : resource.booking_issues.every(
            (issue) => issue.status === "rejected"
          ) ? (
          <CheckCircle color="inherit" />
        ) : (
          <ErrorRounded color="inherit" />
        )}
      </Tooltip>
    );
  };

  // Determine the custom resource name
  let customResourceName = undefined;
  let customLabel = undefined;

  if (isNew && hasRevisions) {
    customResourceName = "REVISE";
  } else if (isAllDeliveriesVerified) {
    const status = translateStatus(resource.status);
    customLabel = `${resource.id} ${status} - VERIFIED`;
  }

  return (
    <MyChip
      isEOT={resource.is_eot}
      tooltip={renderTooltipIcon()}
      customResourceName={customResourceName}
      customLabel={customLabel}
      resource={resource}
      resourceName="bookings"
      avatarCharacter="BK"
      {...rest}
    />
  );
};

export const AllocationChip: React.FC<Props> = ({ resource, ...rest }) => (
  <MyChip
    resource={resource}
    resourceName="allocations"
    avatarCharacter="AL"
    {...rest}
  />
);

export const DeliveryChip: React.FC<{ resource: Delivery }> = ({
  resource,
  ...rest
}) => {
  const isVerified = resource.status === "delivered" && resource.verify_at;

  const customLabel = isVerified
    ? `${resource.id} ${translateStatus(resource.status)} - VERIFIED`
    : undefined;

  return (
    <MyChip
      resource={resource}
      resourceName="deliveries"
      avatarCharacter="DL"
      customLabel={customLabel}
      {...rest}
    />
  );
};

export const ProductInstanceChip: React.FC<{ resource: ProductInstance }> = ({
  resource,
  ...rest
}) => {
  const backgroundColor = get(
    resource,
    "product.business_unit.color_hex",
    "#aaa"
  );

  return (
    <MyChip
      avatarAction={resource.is_consigned ? "CS" : ""}
      resource={resource}
      resourceName="product-instances"
      avatarCharacter="ST"
      style={{ backgroundColor }}
      {...rest}
    />
  );
};
